import { Grid } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import contractAIQConstants from '../../../core-utils/messages/ContractAIQConstants';
import {
  addContract,
  getContracts,
  getServiceAgreements,
  retryFailed,
  uploadContracts,
} from '../../../core-utils/axios/compliance-assurance/contracts';
import { EXPERICENCE_SCREEN } from '../../../core-utils/routes';
import Loader from '../../molecules/Loader';
import { useContractReviewState } from '../../../core-utils/custom-hooks/Contracts/contractPolling';
import { ContractTableRow } from '../../../core-utils/types/contracts.types';
import { formatContractsData } from '../../../core-utils/helper-utils/contracts.helper';
import { ReviewDoc } from './components/ContractReviewRow';
import FileUploadSuccess from './components/FileUploadSuccess';
import UploadFilesSection from './components/UploadFilesSection';
import ContractsReview from './components/ContractsReview';
import CreatedContracts from './components/CreatedContracts';
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from 'react-router-dom';

type ContractStage =
  | 'Upload'
  | 'Upload Success'
  | 'Review'
  | 'Created Contracts';

export interface ContractsUploadTabProps {
  egNid: string;
  employer: string;
  /** old page's snackbar opener: (message, severity, duration) */
   
  openSnackbar: (message: string, severity: MuiColor, duration: number) => void;
  onProceedClick: () => void;
  onCreateContracts?: () => void;
}

const useStyles = makeStyles()({
  contractsUpload: { padding: '25px 24px 28px 24px' },
  buttonsGrid: { display: 'flex', justifyContent: 'flex-end' },
  fileUploadSuccess: {
    padding: '25px 24px 28px 24px',
    minHeight: '476px',
    display: 'flex',
  },
});

const MAX_FILE_MB = process.env.MAX_FILE_MB
  ? parseInt(process.env.MAX_FILE_MB)
  : 50;

const CONTRACT_REQUIRED_FIELDS: (keyof ReviewDoc)[] = [
  'service_agreement_name',
  'health_care_partner',
  'document_type',
];

const ContractsUploadTab: React.FC<ContractsUploadTabProps> = ({
  egNid,
  employer,
  openSnackbar,
  onProceedClick,
  onCreateContracts,
}) => {
  const { classes } = useStyles();
  const messages = contractAIQConstants.COMPLIANCE_ASSURANCE;
  const navigate = useNavigate();

  // ---- State ----
  const [contractStage, setContractStage] = useState<ContractStage>('Upload');
  const [selectedContractFiles, setSelectedContractFiles] = useState<any[]>([]);
  const [contractLoading, setContractLoading] = useState<boolean>(false);
  const [reviewDocs, setReviewDocs] = useState<ReviewDoc[]>([]);
  const [serviceAgreements, setServiceAgreements] = useState<any[]>([]);
  const [healthcarePartners, setHealthcarePartners] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [contractsTableData, setContractsTableData] = useState<
    ContractTableRow[]
  >([]);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryingAll, setRetryingAll] = useState<boolean>(false);

  // ---- Derived ----
  const contractsOversizeError = useMemo(
    () =>
      selectedContractFiles
        .filter((f: any) => f?.size && f.size > MAX_FILE_MB * 1024 * 1024)
        .map((f: any) => f?.name),
    [selectedContractFiles],
  );

  const contractsDuplicateError = useMemo(() => {
    const counts: Record<string, number> = {};
    selectedContractFiles.forEach((f: any) => {
      if (f?.name) {
        counts[f.name] = (counts[f.name] || 0) + 1;
      }
    });
    return Object.keys(counts).filter((n) => counts[n] > 1);
  }, [selectedContractFiles]);

  const isContractsButtonDisabled = useMemo(
    () =>
      selectedContractFiles.length === 0 ||
      contractsOversizeError.length > 0 ||
      contractsDuplicateError.length > 0,
    [selectedContractFiles, contractsOversizeError, contractsDuplicateError],
  );

  const {
    counts: reviewCounts,
    documents: reviewBuckets,
    refresh: refreshReviewState,
  } = useContractReviewState({ egNid });

  // Service agreements for the review dropdowns (once).
  useEffect(() => {
    (async () => {
      try {
        const data = await getServiceAgreements();
        setServiceAgreements(data?.data || data || []);
      } catch (e) {
        /* non-blocking */
      }
    })();
  }, []);

  // On mount: one review-state fetch decides Review vs Upload.
  useEffect(() => {
    if (!egNid) {
      return;
    }
    (async () => {
      setContractLoading(true);
      const s = await refreshReviewState();
      setContractStage(s && s !== 'done' ? 'Review' : 'Upload');
      setContractLoading(false);
    })();
  }, [egNid]);

  // Map ready docs -> editable rows, preserving user edits by document_id.
  useEffect(() => {
    const ready = reviewBuckets.readyToReview || [];
    setReviewDocs((prev) => {
       
      const prevById = new Map(prev.map((d) => [d.document_id, d]));
       
      return ready.map((item: any) => {
        const existing = prevById.get(item._id);
        if (existing) {
          return existing;
        }
        return {
          document_id: item._id,
          document_name: item.document_name,
          document_url: item.document_url,
          service_agreement_name: item.extracted_metadata?.vendor_type || '',
          health_care_partner: item.extracted_metadata?.vendor_name || '',
          document_type:
            item.document_type || item.extracted_metadata?.document_type || '',
          document_effective_date:
            item.document_effective_date ||
            item.extracted_metadata?.effective_date ||
            '',
        } as ReviewDoc;
      });
    });

    const names = ready
      .map((i: any) => i.extracted_metadata?.vendor_name)
      .filter(Boolean);
    if (names.length) {
      setHealthcarePartners((prev) => Array.from(new Set([...prev, ...names])));
    }
  }, [reviewBuckets.readyToReview]);

  // ---- Field handlers ----
  const handleFieldChange = (
    documentId: string,
    field: keyof ReviewDoc,
    value: string,
  ) => {
    setReviewDocs((prev) =>
      prev.map((d) =>
        d.document_id === documentId
          ? {
              ...d,
              [field]: value,
              ...(field === 'service_agreement_name'
                ? { health_care_partner: '' }
                : {}),
            }
          : d,
      ),
    );

    if (value && String(value).trim() !== '') {
      setValidationErrors((prev) => {
        const forDoc = prev[documentId];
        if (!forDoc) {
          return prev;
        }
        const next = forDoc.filter((f) => f !== field);
        const updated =
          field === 'service_agreement_name'
            ? Array.from(new Set([...next, 'health_care_partner']))
            : next;
        return { ...prev, [documentId]: updated };
      });
    }
  };

  const handlePartnerInput = (documentId: string, value: string) => {
    setHealthcarePartners((prev) => Array.from(new Set([...prev, value])));
    setReviewDocs((prev) =>
      prev.map((d) =>
        d.document_id === documentId ? { ...d, health_care_partner: value } : d,
      ),
    );
    if (value && value.trim() !== '') {
      setValidationErrors((prev) => {
        const forDoc = prev[documentId];
        if (!forDoc) {
          return prev;
        }
        return {
          ...prev,
          [documentId]: forDoc.filter((f) => f !== 'health_care_partner'),
        };
      });
    }
  };

  // ---- API integration ----
  const fetchContracts = async () => {
    setContractLoading(true);
    try {
      const res = await getContracts(egNid, false); // active only
      const data = res?.data || [];
      const submitted = reviewDocs.map((d) => ({ document_id: d.document_id }));
      setContractsTableData(
        data.map((item: any) => formatContractsData(item, submitted)),
      );
    } catch (e: any) {
      openSnackbar(
        e?.response?.data?.error || 'Failed to fetch contracts',
        'error',
        6000,
      );
    } finally {
      setContractLoading(false);
    }
  };

  const handleUploadContracts = async () => {
    if (
      contractsOversizeError.length > 0 ||
      contractsDuplicateError.length > 0 ||
      selectedContractFiles.length === 0
    ) {
      return;
    }

    setContractLoading(true);
    try {
      const data = await uploadContracts(
        selectedContractFiles,
        egNid,
        employer,
      );
      const { uploaded = [], failed = [] } = data || {};

      if (uploaded.length === 0) {
        openSnackbar(
          'No files could be uploaded. Please retry.',
          'error',
          6000,
        );
        return;
      }

      if (failed.length > 0) {
        openSnackbar(
          `${uploaded.length} uploaded, ${failed.length} failed. You can retry the failed ones.`,
          'warning',
          6000,
        );
      }

      setContractStage('Upload Success');
    } catch (error: any) {
      openSnackbar(
        error?.response?.data?.error || 'Upload failed',
        'error',
        6000,
      );
    } finally {
      setContractLoading(false);
    }
  };

  const handleCreateContracts = async () => {
    const errors: Record<string, string[]> = {};
    reviewDocs.forEach((doc) => {
      const missing = CONTRACT_REQUIRED_FIELDS.filter(
        (f) => !doc[f] || String(doc[f]).trim() === '',
      );
      if (missing.length > 0) {
        errors[doc.document_id] = missing;
      }
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      openSnackbar(
        'Please fill all required fields before creating contracts.',
        'error',
        5000,
      );
      return;
    }

    setContractLoading(true);
    setContractStage('Created Contracts');
    try {
      const res = await addContract(egNid, reviewDocs);

      if (res.failed > 0 && res.succeeded > 0) {
        const reasons = res.failures
          .map(
            (f: { document_name: string; reason: string }) =>
              `• ${f.document_name}: ${f.reason}`,
          )
          .join('\n');
        openSnackbar(
          `${res.succeeded} contract${res.succeeded > 1 ? 's' : ''} created.\n${
            res.failed
          } could not be added:\n${reasons}`,
          'warning',
          12000,
        );
      } else if (res.succeeded === 0) {
        const reasons = res.failures
          .map(
            (f: { document_name: string; reason: string }) =>
              `• ${f.document_name}: ${f.reason}`,
          )
          .join('\n');
        openSnackbar(`No contracts created:\n${reasons}`, 'error', 8000);
      } else {
        openSnackbar(res.message || 'Contracts created.', 'success', 4000);
      }
      onCreateContracts;
      await fetchContracts();
    } catch (error: any) {
      openSnackbar(
        error?.response?.data?.error || 'Failed to create contracts',
        'error',
        6000,
      );
    } finally {
      setContractLoading(false);
    }
  };

  const handleRetryOne = async (documentId: string) => {
    setRetryingId(documentId);
    try {
      const res = await retryFailed(egNid, documentId);
      openSnackbar(
        `Retrying ${res.retried} document${res.retried > 1 ? 's' : ''}…`,
        'info',
        3000,
      );
      await refreshReviewState();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      openSnackbar(err?.response?.data?.error || 'Retry failed', 'error', 6000);
    } finally {
      setRetryingId(null);
    }
  };

  const handleRetryAll = async () => {
    setRetryingAll(true);
    try {
      const res = await retryFailed(egNid);
      openSnackbar(
        `Retrying ${res.retried} document${res.retried > 1 ? 's' : ''}…`,
        'info',
        3000,
      );
      await refreshReviewState();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      openSnackbar(err?.response?.data?.error || 'Retry failed', 'error', 6000);
    } finally {
      setRetryingAll(false);
    }
  };

  // ---- Render ----
  if (contractLoading) {
    return <Loader height="476px" />;
  }

  switch (contractStage) {
    case 'Upload':
      return (
        <Grid className={classes.contractsUpload}>
          <Typography
            fontSize="16px"
            fontType="semiBold"
            lineHeight="24px"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="0px 0px 24px 0px"
          >
            {messages.UPLOAD_CONTRACTS}
          </Typography>
          <UploadFilesSection
            uploadedFiles={selectedContractFiles}
            setUploadedFiles={setSelectedContractFiles}
            uploadInfo={messages.UPLOAD_INFO}
            maxFileMB={MAX_FILE_MB}
            duplicateError={contractsDuplicateError}
            oversizeError={contractsOversizeError}
            isMultiple={true}
          />
          <Grid className={classes.buttonsGrid}>
            <Button
              label={'Upload Files'}
              variant="contained"
              height="37px"
              width="auto"
              disabled={isContractsButtonDisabled || contractLoading}
              onClick={handleUploadContracts}
            />
          </Grid>
        </Grid>
      );

    case 'Upload Success':
      return (
        <Grid className={classes.fileUploadSuccess}>
          <FileUploadSuccess
            count={selectedContractFiles.length}
            onLeave={() => navigate(EXPERICENCE_SCREEN)}
          />
        </Grid>
      );

    case 'Review':
      return (
        <Grid className={classes.contractsUpload}>
          <Typography
            fontSize="16px"
            fontType="semiBold"
            lineHeight="24px"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="0px 0px 16px 0px"
          >
            {messages.UPLOAD_CONTRACTS}
          </Typography>

          <ContractsReview
            readyDocs={reviewDocs}
            processingDocs={reviewBuckets.processing || []}
            failedDocs={reviewBuckets.failed || []}
            counts={reviewCounts}
            serviceAgreements={serviceAgreements}
            healthcarePartners={healthcarePartners}
            docTypeOptions={messages.DOC_TYPE_OPTIONS}
            onFieldChange={handleFieldChange}
            onPartnerInput={handlePartnerInput}
            onCreateContracts={handleCreateContracts}
            validationErrors={validationErrors}
            uploadZone={
              <>
                <UploadFilesSection
                  uploadedFiles={selectedContractFiles}
                  setUploadedFiles={setSelectedContractFiles}
                  uploadInfo={messages.UPLOAD_INFO}
                  maxFileMB={MAX_FILE_MB}
                  duplicateError={contractsDuplicateError}
                  oversizeError={contractsOversizeError}
                  isMultiple={true}
                />
                <Grid className={classes.buttonsGrid}>
                  <Button
                    label="Upload Files"
                    variant="contained"
                    height="37px"
                    width="auto"
                    disabled={isContractsButtonDisabled || contractLoading}
                    onClick={handleUploadContracts}
                  />
                </Grid>
              </>
            }
            retryingAll={retryingAll}
            retryingId={retryingId}
            onRetryOne={handleRetryOne}
            onRetryAll={handleRetryAll}
          />
        </Grid>
      );

    case 'Created Contracts':
      return (
        <CreatedContracts
          rowsData={contractsTableData}
          onProceedClick={onProceedClick}
        />
      );

    default:
      return <></>;
  }
};

export default ContractsUploadTab;
